export interface HVADynamicConversationModalProps {
  show: boolean;
  onHide: () => void;
  agentCode: string;
  agentName: string;
  agentImage?: string;
  onError?: (error: Error) => void;
  customer?: string;
  dni?: string;
  dateOfBirth?: Date;
  loanType?: number;
}

export interface FeedbackMessage {
  id: string;
  timestamp: Date;
  type: 'info' | 'validation' | 'transfer' | 'close';
  message: string;
  details?: string;
  success?: boolean;
}

interface LoanDetails {
  TAE: string;
  Importe: string;
  Plazo: string;
  CuotaMensual: string;
  UltimaCuota: string;
  Total: string;
  TotalIntereses: string;
}

interface LoanVariantes {
  [key: `Variante${number}`]: LoanDetails;
}

interface LoanTipoInfo {
  ImporteMaximo: string;
  PlazoMaximo: string;
  TIN: string;
  Prestamos: LoanVariantes;
}

// eslint-disable-next-line
export type LoanTypeKeys = 'tipo_1' | 'tipo_2' | 'tipo_3' | 'tipo_4' | 'tipo_patrimonial';

interface LoanTipo extends Record<LoanTypeKeys, LoanTipoInfo> {}

interface Loans {
  prestamos: LoanTipo;
}

export const getLoanTypeKey = (type: number | string): LoanTypeKeys => {
  const key = `tipo_${type}`;
  if (isLoanTypeKey(key)) {
    return key;
  }
  throw new Error(`Invalid loan type: ${type}`);
};

function isLoanTypeKey(key: string): key is LoanTypeKeys {
  // eslint-disable-next-line
  return ['tipo_1', 'tipo_2', 'tipo_3', 'tipo_4', 'tipo_patrimonial'].includes(key);
}

// eslint-disable-next-line
export { type Loans, type LoanDetails, type LoanTipo, type LoanTipoInfo, type LoanVariantes };

export const LOANS: Loans = {
  prestamos: {
    tipo_1: {
      ImporteMaximo: '30.000 €',
      PlazoMaximo: '84 meses',
      TIN: '6,95%',
      Prestamos: {
        Variante1: {
          TAE: '7,18%',
          Importe: '30.000 €',
          Plazo: '84 meses',
          CuotaMensual: '452,05 €',
          UltimaCuota: '451,82 €',
          Total: '37.971,97 €',
          TotalIntereses: '7.971,97 €'
        },
        Variante2: {
          TAE: '7,18%',
          Importe: '30.000 €',
          Plazo: '60 meses',
          CuotaMensual: '593,33 €',
          UltimaCuota: '593,23 €',
          Total: '35.599,70 €',
          TotalIntereses: '5.599,70 €'
        },
        Variante3: {
          TAE: '7,18%',
          Importe: '27.000 €',
          Plazo: '84 meses',
          CuotaMensual: '406,84 €',
          UltimaCuota: '407,14 €',
          Total: '34.174,86 €',
          TotalIntereses: '7.174,86 €'
        },
        Variante4: {
          TAE: '7,18%',
          Importe: '27.000 €',
          Plazo: '60 meses',
          CuotaMensual: '534,00 €',
          UltimaCuota: '533,72 €',
          Total: '32.039,72 €',
          TotalIntereses: '5.039,72 €'
        },
        Variante5: {
          TAE: '7,18%',
          Importe: '24.000 €',
          Plazo: '84 meses',
          CuotaMensual: '361,64 €',
          UltimaCuota: '361,64 €',
          Total: '30.377,76 €',
          TotalIntereses: '6.377,76 €'
        },
        Variante6: {
          TAE: '7,18%',
          Importe: '24.000 €',
          Plazo: '48 meses',
          CuotaMensual: '574,15 €',
          UltimaCuota: '574,30 €',
          Total: '27.559,35 €',
          TotalIntereses: '3.559,35 €'
        }
      }
    },
    tipo_2: {
      ImporteMaximo: '22.000 €',
      PlazoMaximo: '84 meses',
      TIN: '6,95%',
      Prestamos: {
        Variante1: {
          TAE: '7,18%',
          Importe: '22.000 €',
          Plazo: '84 meses',
          CuotaMensual: '331,50 €',
          UltimaCuota: '331,67 €',
          Total: '27.846,17 €',
          TotalIntereses: '5.846,17 €'
        },
        Variante2: {
          TAE: '7,18%',
          Importe: '22.000 €',
          Plazo: '48 meses',
          CuotaMensual: '526,31 €',
          UltimaCuota: '526,17 €',
          Total: '25.262,74 €',
          TotalIntereses: '3.262,74 €'
        },
        Variante3: {
          TAE: '7,18%',
          Importe: '20.000 €',
          Plazo: '84 meses',
          CuotaMensual: '301,36 €',
          UltimaCuota: '301,93 €',
          Total: '25.314,81 €',
          TotalIntereses: '5.314,81 €'
        },
        Variante4: {
          TAE: '7,18%',
          Importe: '20.000 €',
          Plazo: '48 meses',
          CuotaMensual: '478,46 €',
          UltimaCuota: '478,53 €',
          Total: '22.966,15 €',
          TotalIntereses: '2.966,15 €'
        },
        Variante5: {
          TAE: '7,18%',
          Importe: '18.000 €',
          Plazo: '84 meses',
          CuotaMensual: '271,23 €',
          UltimaCuota: '271,03 €',
          Total: '22.783,12 €',
          TotalIntereses: '4.783,12 €'
        },
        Variante6: {
          TAE: '7,18%',
          Importe: '18.000 €',
          Plazo: '48 meses',
          CuotaMensual: '430,61 €',
          UltimaCuota: '430,91 €',
          Total: '20.669,58 €',
          TotalIntereses: '2.669,58 €'
        },
        Variante7: {
          TAE: '7,18%',
          Importe: '17.000 €',
          Plazo: '84 meses',
          CuotaMensual: '256,16 €',
          UltimaCuota: '256,19 €',
          Total: '21.517,47 €',
          TotalIntereses: '4.517,47 €'
        },
        Variante8: {
          TAE: '7,18%',
          Importe: '17.000 €',
          Plazo: '48 meses',
          CuotaMensual: '406,69 €',
          UltimaCuota: '406,76 €',
          Total: '19.521,19 €',
          TotalIntereses: '2.521,19 €'
        },
        Variante9: {
          TAE: '7,18%',
          Importe: '16.000 €',
          Plazo: '84 meses',
          CuotaMensual: '241,09 €',
          UltimaCuota: '241,32 €',
          Total: '20.251,79 €',
          TotalIntereses: '4.251,79 €'
        },
        Variante10: {
          TAE: '7,18%',
          Importe: '16.000 €',
          Plazo: '48 meses',
          CuotaMensual: '382,77 €',
          UltimaCuota: '382,74 €',
          Total: '18.372,93 €',
          TotalIntereses: '2.372,93 €'
        },
        Variante11: {
          TAE: '7,18%',
          Importe: '15.000 €',
          Plazo: '84 meses',
          CuotaMensual: '226,02 €',
          UltimaCuota: '226,44 €',
          Total: '18.986,10 €',
          TotalIntereses: '3.986,10 €'
        },
        Variante12: {
          TAE: '7,18%',
          Importe: '15.000 €',
          Plazo: '48 meses',
          CuotaMensual: '358,85 €',
          UltimaCuota: '358,64 €',
          Total: '17.224,59 €',
          TotalIntereses: '2.224,59 €'
        },
        Variante13: {
          TAE: '7,18%',
          Importe: '14.000 €',
          Plazo: '72 meses',
          CuotaMensual: '238,35 €',
          UltimaCuota: '238,36 €',
          Total: '17.161,21 €',
          TotalIntereses: '3.161,21 €'
        },
        Variante14: {
          TAE: '7,184%',
          Importe: '14.000 €',
          Plazo: '36 meses',
          CuotaMensual: '431,96 €',
          UltimaCuota: '431,91 €',
          Total: '15.550,51 €',
          TotalIntereses: '1.550,51 €'
        },
        Variante15: {
          TAE: '7,18%',
          Importe: '13.000 €',
          Plazo: '72 meses',
          CuotaMensual: '221,33 €',
          UltimaCuota: '220,02 €',
          Total: '15.934,45 €',
          TotalIntereses: '2.934,45 €'
        },
        Variante16: {
          TAE: '7,184%',
          Importe: '13.000 €',
          Plazo: '36 meses',
          CuotaMensual: '401,11 €',
          UltimaCuota: '400,93 €',
          Total: '14.439,78 €',
          TotalIntereses: '1.439,78 €'
        },
        Variante17: {
          TAE: '7,18%',
          Importe: '12.000 €',
          Plazo: '72 meses',
          CuotaMensual: '204,30 €',
          UltimaCuota: '204,26 €',
          Total: '14.709,56 €',
          TotalIntereses: '2.709,56 €'
        },
        Variante18: {
          TAE: '7,184%',
          Importe: '12.000 €',
          Plazo: '36 meses',
          CuotaMensual: '370,25 €',
          UltimaCuota: '370,31 €',
          Total: '13.329,06 €',
          TotalIntereses: '1.329,06 €'
        },
        Variante19: {
          TAE: '7,18%',
          Importe: '11.000 €',
          Plazo: '72 meses',
          CuotaMensual: '187,28 €',
          UltimaCuota: '186,90 €',
          Total: '13.483,78 €',
          TotalIntereses: '2.483,78 €'
        },
        Variante20: {
          TAE: '7,184%',
          Importe: '11.000 €',
          Plazo: '36 meses',
          CuotaMensual: '339,40 €',
          UltimaCuota: '339,28 €',
          Total: '12.218,28 €',
          TotalIntereses: '1.218,28 €'
        },
        Variante21: {
          TAE: '7,18%',
          Importe: '10.000 €',
          Plazo: '72 meses',
          CuotaMensual: '170,25 €',
          UltimaCuota: '170,29 €',
          Total: '12.258,04 €',
          TotalIntereses: '2.258,04 €'
        },
        Variante22: {
          TAE: '7,184%',
          Importe: '10.000 €',
          Plazo: '36 meses',
          CuotaMensual: '308,54 €',
          UltimaCuota: '308,65 €',
          Total: '11.107,55 €',
          TotalIntereses: '1.107,55 €'
        },
        Variante23: {
          TAE: '7,18%',
          Importe: '9.000 €',
          Plazo: '72 meses',
          CuotaMensual: '153,23 €',
          UltimaCuota: '152,81 €',
          Total: '11.032,14 €',
          TotalIntereses: '2.032,14 €'
        },
        Variante24: {
          TAE: '7,184%',
          Importe: '9.000 €',
          Plazo: '36 meses',
          CuotaMensual: '277,69 €',
          UltimaCuota: '277,62 €',
          Total: '9.996,77 €',
          TotalIntereses: '996,77 €'
        }
      }
    },
    tipo_3: {
      ImporteMaximo: '16.000 €',
      PlazoMaximo: '84 meses',
      TIN: '6,95%',
      Prestamos: {
        Variante1: {
          TAE: '7,18%',
          Importe: '16.000 €',
          Plazo: '84 meses',
          CuotaMensual: '241,09 €',
          UltimaCuota: '241,09 €',
          Total: '20.251,56 €',
          TotalIntereses: '4.251,56 €'
        },
        Variante2: {
          TAE: '7,18%',
          Importe: '16.000 €',
          Plazo: '48 meses',
          CuotaMensual: '382,77 €',
          UltimaCuota: '382,74 €',
          Total: '18.372,93 €',
          TotalIntereses: '2.372,93 €'
        },
        Variante3: {
          TAE: '7,18%',
          Importe: '15.000 €',
          Plazo: '84 meses',
          CuotaMensual: '226,02 €',
          UltimaCuota: '226,44 €',
          Total: '18.986,10 €',
          TotalIntereses: '3.986,10 €'
        },
        Variante4: {
          TAE: '7,18%',
          Importe: '15.000 €',
          Plazo: '48 meses',
          CuotaMensual: '358,85 €',
          UltimaCuota: '358,64 €',
          Total: '17.224,59 €',
          TotalIntereses: '2.224,59 €'
        },
        Variante5: {
          TAE: '7,18%',
          Importe: '14.000 €',
          Plazo: '72 meses',
          CuotaMensual: '238,35 €',
          UltimaCuota: '238,36 €',
          Total: '17.161,21 €',
          TotalIntereses: '3.161,21 €'
        },
        Variante6: {
          TAE: '7,18%',
          Importe: '14.000 €',
          Plazo: '36 meses',
          CuotaMensual: '431,96 €',
          UltimaCuota: '431,91 €',
          Total: '15.550,51 €',
          TotalIntereses: '1.550,51 €'
        },
        Variante7: {
          TAE: '7,18%',
          Importe: '13.000 €',
          Plazo: '72 meses',
          CuotaMensual: '221,33 €',
          UltimaCuota: '220,92 €',
          Total: '15.935,35 €',
          TotalIntereses: '2.935,35 €'
        },
        Variante8: {
          TAE: '7,18%',
          Importe: '13.000 €',
          Plazo: '36 meses',
          CuotaMensual: '401,11 €',
          UltimaCuota: '400,93 €',
          Total: '14.439,78 €',
          TotalIntereses: '1.439,78 €'
        },
        Variante9: {
          TAE: '7,18%',
          Importe: '12.000 €',
          Plazo: '72 meses',
          CuotaMensual: '204,30 €',
          UltimaCuota: '204,26 €',
          Total: '14.709,56 €',
          TotalIntereses: '2.709,56 €'
        },
        Variante10: {
          TAE: '7,18%',
          Importe: '12.000 €',
          Plazo: '36 meses',
          CuotaMensual: '370,25 €',
          UltimaCuota: '370,31 €',
          Total: '13.329,06 €',
          TotalIntereses: '1.329,06 €'
        },
        Variante11: {
          TAE: '7,18%',
          Importe: '11.000 €',
          Plazo: '72 meses',
          CuotaMensual: '187,28 €',
          UltimaCuota: '186,90 €',
          Total: '13.483,78 €',
          TotalIntereses: '2.483,78 €'
        },
        Variante12: {
          TAE: '7,18%',
          Importe: '11.000 €',
          Plazo: '36 meses',
          CuotaMensual: '339,40 €',
          UltimaCuota: '339,28 €',
          Total: '12.218,28 €',
          TotalIntereses: '1.218,28 €'
        },
        Variante13: {
          TAE: '7,18%',
          Importe: '10.000 €',
          Plazo: '72 meses',
          CuotaMensual: '170,25 €',
          UltimaCuota: '170,29 €',
          Total: '12.258,04 €',
          TotalIntereses: '2.258,04 €'
        },
        Variante14: {
          TAE: '7,18%',
          Importe: '10.000 €',
          Plazo: '36 meses',
          CuotaMensual: '308,54 €',
          UltimaCuota: '308,65 €',
          Total: '11.107,55 €',
          TotalIntereses: '1.107,55 €'
        },
        Variante15: {
          TAE: '7,18%',
          Importe: '9.000 €',
          Plazo: '72 meses',
          CuotaMensual: '153,23 €',
          UltimaCuota: '152,81 €',
          Total: '11.032,14 €',
          TotalIntereses: '2.032,14 €'
        },
        Variante16: {
          TAE: '7,18%',
          Importe: '9.000 €',
          Plazo: '36 meses',
          CuotaMensual: '277,69 €',
          UltimaCuota: '277,62 €',
          Total: '9.996,77 €',
          TotalIntereses: '996,77 €'
        },
        Variante17: {
          TAE: '7,18%',
          Importe: '8.000 €',
          Plazo: '72 meses',
          CuotaMensual: '136,20 €',
          UltimaCuota: '136,18 €',
          Total: '9.806,38 €',
          TotalIntereses: '1.806,38 €'
        },
        Variante18: {
          TAE: '7,18%',
          Importe: '8.000 €',
          Plazo: '36 meses',
          CuotaMensual: '246,83 €',
          UltimaCuota: '246,96 €',
          Total: '8.886,01 €',
          TotalIntereses: '886,01 €'
        },
        Variante19: {
          TAE: '7,18%',
          Importe: '7.000 €',
          Plazo: '72 meses',
          CuotaMensual: '119,18 €',
          UltimaCuota: '118,74 €',
          Total: '8.580,52 €',
          TotalIntereses: '1.580,52 €'
        },
        Variante20: {
          TAE: '7,18%',
          Importe: '7.000 €',
          Plazo: '36 meses',
          CuotaMensual: '215,98 €',
          UltimaCuota: '215,96 €',
          Total: '7.775,26 €',
          TotalIntereses: '775,26 €'
        },
        Variante21: {
          TAE: '7,18%',
          Importe: '6.000 €',
          Plazo: '60 meses',
          CuotaMensual: '118,67 €',
          UltimaCuota: '118,39 €',
          Total: '7.119,92 €',
          TotalIntereses: '1.119,92 €'
        },
        Variante22: {
          TAE: '7,18%',
          Importe: '6.000 €',
          Plazo: '36 meses',
          CuotaMensual: '185,13 €',
          UltimaCuota: '184,98 €',
          Total: '6.664,53 €',
          TotalIntereses: '664,53 €'
        }
      }
    },
    tipo_4: {
      ImporteMaximo: '8.000 €',
      PlazoMaximo: '72 meses',
      TIN: '6,95%',
      Prestamos: {
        Variante1: {
          TAE: '7,18%',
          Importe: '8.000 €',
          Plazo: '72 meses',
          CuotaMensual: '136,20 €',
          UltimaCuota: '136,18 €',
          Total: '9.806,38 €',
          TotalIntereses: '1.806,38 €'
        },
        Variante2: {
          TAE: '7,18%',
          Importe: '8.000 €',
          Plazo: '36 meses',
          CuotaMensual: '246,83 €',
          UltimaCuota: '246,96 €',
          Total: '8.886,01 €',
          TotalIntereses: '886,01 €'
        },
        Variante3: {
          TAE: '7,18%',
          Importe: '7.000 €',
          Plazo: '72 meses',
          CuotaMensual: '119,18 €',
          UltimaCuota: '118,70 €',
          Total: '8.580,48 €',
          TotalIntereses: '1.580,48 €'
        },
        Variante4: {
          TAE: '7,18%',
          Importe: '7.000 €',
          Plazo: '36 meses',
          CuotaMensual: '215,98 €',
          UltimaCuota: '215,96 €',
          Total: '7.775,26 €',
          TotalIntereses: '775,26 €'
        },
        Variante5: {
          TAE: '7,18%',
          Importe: '6.000 €',
          Plazo: '60 meses',
          CuotaMensual: '118,67 €',
          UltimaCuota: '118,39 €',
          Total: '7.119,92 €',
          TotalIntereses: '1.119,92 €'
        },
        Variante6: {
          TAE: '7,18%',
          Importe: '6.000 €',
          Plazo: '36 meses',
          CuotaMensual: '185,13 €',
          UltimaCuota: '184,98 €',
          Total: '6.664,53 €',
          TotalIntereses: '664,53 €'
        },
        Variante7: {
          TAE: '7,18%',
          Importe: '5.000 €',
          Plazo: '60 meses',
          CuotaMensual: '98,89 €',
          UltimaCuota: '98,77 €',
          Total: '5.933,28 €',
          TotalIntereses: '933,28 €'
        },
        Variante8: {
          TAE: '7,18%',
          Importe: '5.000 €',
          Plazo: '36 meses',
          CuotaMensual: '154,27 €',
          UltimaCuota: '154,33 €',
          Total: '5.553,78 €',
          TotalIntereses: '553,78 €'
        },
        Variante9: {
          TAE: '7,18%',
          Importe: '4.000 €',
          Plazo: '60 meses',
          CuotaMensual: '79,11 €',
          UltimaCuota: '79,16 €',
          Total: '4.746,65 €',
          TotalIntereses: '746,65 €'
        },
        Variante10: {
          TAE: '7,18%',
          Importe: '4.000 €',
          Plazo: '36 meses',
          CuotaMensual: '123,42 €',
          UltimaCuota: '123,30 €',
          Total: '4.443,00 €',
          TotalIntereses: '443,00 €'
        },
        Variante11: {
          TAE: '7,18%',
          Importe: '3.000 €',
          Plazo: '48 meses',
          CuotaMensual: '71,77 €',
          UltimaCuota: '71,70 €',
          Total: '3.444,89 €',
          TotalIntereses: '444,89 €'
        },
        Variante12: {
          TAE: '7,18%',
          Importe: '3.000 €',
          Plazo: '36 meses',
          CuotaMensual: '92,56 €',
          UltimaCuota: '92,64 €',
          Total: '3.332,24 €',
          TotalIntereses: '332,24 €'
        },
        Variante13: {
          TAE: '7,19%',
          Importe: '3.000 €',
          Plazo: '18 meses',
          CuotaMensual: '175,99 €',
          UltimaCuota: '175,93 €',
          Total: '3.167,76 €',
          TotalIntereses: '167,76 €'
        },
        Variante14: {
          TAE: '7,19%',
          Importe: '3.000 €',
          Plazo: '18 meses',
          CuotaMensual: '175,99 €',
          UltimaCuota: '175,93 €',
          Total: '3.167,76 €',
          TotalIntereses: '167,76 €'
        },
        Variante15: {
          TAE: '7,18%',
          Importe: '2.000 €',
          Plazo: '48 meses',
          CuotaMensual: '47,85 €',
          UltimaCuota: '47,60 €',
          Total: '2.296,55 €',
          TotalIntereses: '296,55 €'
        },
        Variante16: {
          TAE: '7,18%',
          Importe: '2.000 €',
          Plazo: '36 meses',
          CuotaMensual: '61,71 €',
          UltimaCuota: '61,66 €',
          Total: '2.221,51 €',
          TotalIntereses: '221,51 €'
        },
        Variante17: {
          TAE: '7,19%',
          Importe: '2.000 €',
          Plazo: '18 meses',
          CuotaMensual: '117,32 €',
          UltimaCuota: '117,42 €',
          Total: '2.111,86 €',
          TotalIntereses: '111,86 €'
        },
        Variante18: {
          TAE: '7,19%',
          Importe: '2.000 €',
          Plazo: '18 meses',
          CuotaMensual: '117,32 €',
          UltimaCuota: '117,42 €',
          Total: '2.111,86 €',
          TotalIntereses: '111,86 €'
        },
        Variante19: {
          TAE: '7,18%',
          Importe: '1.500 €',
          Plazo: '48 meses',
          CuotaMensual: '35,88 €',
          UltimaCuota: '36,11 €',
          Total: '1.722,47 €',
          TotalIntereses: '222,47 €'
        },
        Variante20: {
          TAE: '7,18%',
          Importe: '1.500 €',
          Plazo: '36 meses',
          CuotaMensual: '46,28 €',
          UltimaCuota: '46,35 €',
          Total: '1.666,15 €',
          TotalIntereses: '166,15 €'
        },
        Variante21: {
          TAE: '7,19%',
          Importe: '1.500 €',
          Plazo: '18 meses',
          CuotaMensual: '87,99 €',
          UltimaCuota: '88,06 €',
          Total: '1.583,89 €',
          TotalIntereses: '83,89 €'
        },
        Variante22: {
          TAE: '7,19%',
          Importe: '1.500 €',
          Plazo: '18 meses',
          CuotaMensual: '87,99 €',
          UltimaCuota: '88,06 €',
          Total: '1.583,89 €',
          TotalIntereses: '83,89 €'
        }
      }
    },
    tipo_patrimonial: {
      ImporteMaximo: '50.000 €',
      PlazoMaximo: '96 meses',
      TIN: '5,95%',
      Prestamos: {
        Variante1: {
          TAE: '6,11%',
          Importe: '50.000 €',
          Plazo: '96 meses',
          CuotaMensual: '655,85 €',
          UltimaCuota: '656,43 €',
          Total: '62.962,18 €',
          TotalIntereses: '12.962,18 €'
        },
        Variante2: {
          TAE: '6,11%',
          Importe: '50.000 €',
          Plazo: '72 meses',
          CuotaMensual: '827,46 €',
          UltimaCuota: '827,89 €',
          Total: '59.577,55 €',
          TotalIntereses: '9.577,55 €'
        }
      }
    }
  }
};
